/**
 * Board feature domain types (PX-W7.4, TASK-41CE1DE2).
 *
 * `BoardTaskRow` / `BoardEpicRow` mirror the serialized `MongoTask` /
 * `MongoEpic` field-for-field (Jackson `NON_NULL`, so an absent field is
 * omitted rather than `null` — every non-mandatory field below is optional).
 * `TaskStatusValue` is the twelve wire values of `TaskStatus.java`; the five
 * long-form aliases are accepted by the SERVER on write only — the client
 * never sends one (`TASK_STATUS_ALIASES` exists for the contract pin).
 */

/** The twelve `TaskStatus` wire values (`domain/enums/TaskStatus.java`). */
export type TaskStatusValue =
  | 'backlog'
  | 'todo'
  | 'spec'
  | 'plan'
  | 'execute'
  | 'review'
  | 'uat'
  | 'test'
  | 'done'
  | 'blocked'
  | 'archived'
  | 'cancelled'

/** Server-accepted long-form aliases — write-only; the client never emits one. */
export type TaskStatusAlias = 'specifying' | 'planning' | 'executing' | 'reviewing' | 'testing'

/** Alias → canonical value, pinned against `TaskStatus.java`'s `ALIASES` map. */
export const TASK_STATUS_ALIASES: Record<TaskStatusAlias, TaskStatusValue> = {
  specifying: 'spec',
  planning: 'plan',
  executing: 'execute',
  reviewing: 'review',
  testing: 'test'
}

/** Every wire value, in the server enum's declared order. */
export const TASK_STATUS_VALUES: readonly TaskStatusValue[] = [
  'backlog',
  'todo',
  'spec',
  'plan',
  'execute',
  'review',
  'uat',
  'test',
  'done',
  'blocked',
  'archived',
  'cancelled'
]

/**
 * The four columns of the Tasks view, in drawing order
 * (CR-BUILD-BOARD-EPICS-2026-09-26, spec R.6.2): Backlog · Build queue ·
 * Review (UAT) · Done.
 */
export type BoardColumnKey = 'backlog' | 'buildQueue' | 'review' | 'done'

export const BOARD_COLUMNS: readonly BoardColumnKey[] = ['backlog', 'buildQueue', 'review', 'done']

/**
 * Status → column, TOTAL over the twelve wire values by construction of the
 * `Record` (INV-R.1). `blocked` sits in the Build queue (Decision D4);
 * `archived` / `cancelled` are off-board (`null`) — neither read returns them.
 */
export const BOARD_COLUMN_OF: Record<TaskStatusValue, BoardColumnKey | null> = {
  backlog: 'backlog',
  todo: 'backlog',
  spec: 'buildQueue',
  plan: 'buildQueue',
  execute: 'buildQueue',
  review: 'buildQueue',
  test: 'buildQueue',
  blocked: 'buildQueue',
  uat: 'review',
  done: 'done',
  archived: null,
  cancelled: null
}

/** The Build board's two projections of one load; the URL is their only home (INV-R.4). */
export type BoardView = 'epics' | 'tasks'

/** The Tasks view's epic narrowing, parsed from `?epic=` (spec R.6.3). */
export type EpicFilter = { kind: 'all' } | { kind: 'epic'; id: string } | { kind: 'unassigned' }

/** The reserved `?epic=` value (and summary key) of the Unassigned card. */
export const UNASSIGNED_EPIC_KEY = 'none'

/**
 * One Epics-view card (spec R.6.4). Every figure is a count over the loaded
 * tasks + backlog rows — never the server's `progress` / `completedTickets`
 * (INV-R.2): `backlog + buildQueue + awaitingReview + done === total`.
 */
export interface EpicSummary {
  /** The epic's legacyId, or {@link UNASSIGNED_EPIC_KEY}. */
  key: string
  /** `null` for Unassigned, and for an epic the server returned untitled. */
  title: string | null
  total: number
  backlog: number
  buildQueue: number
  awaitingReview: number
  done: number
  /** `done / total`, 0 when `total` is 0. */
  ratio: number
  /** `round(100 · ratio)` — the card's progressbar value (`aria-valuenow`). */
  percent: number
}

export type BoardColumns = Record<BoardColumnKey, BoardTaskRow[]>

/** The four `ArtefactKind` suffixes (`features/board/ArtefactKind.java`). */
export type ArtefactKindSuffix = 'plan' | 'spec' | 'code-changes' | 'tests'

export const ARTEFACT_KIND_SUFFIXES: readonly ArtefactKindSuffix[] = [
  'plan',
  'spec',
  'code-changes',
  'tests'
]

/** Flat assignee/reviewer record embedded on a task (`MongoAssignee`). */
export interface BoardAssignee {
  type: string
  id: string
  name?: string
  email?: string
  avatar?: string
}

/**
 * One task row, mirroring `MongoTask`'s serialized JSON. `id` and `legacyId`
 * carry the SAME `TASK-XXXXXXXX` handle (the class exposes both the raw
 * field and the `@JsonProperty("id")` override) — verdict thunks swap by
 * `legacyId`, matching the routes, which also take the legacy handle.
 */
export interface BoardTaskRow {
  id: string
  legacyId: string
  projectId?: string
  taskType: string
  title: string
  epicId?: string
  storyId?: string
  versionId?: string
  priority?: string
  status: TaskStatusValue
  estimate?: string
  labels?: string[]
  assigneeId?: string
  assignee?: BoardAssignee
  reviewerId?: string
  reviewer?: BoardAssignee
  dependencies?: string[]
  description?: string
  planFiles?: string[]
  codeChangesFiles?: string[]
  testFiles?: string[]
  specFiles?: string[]
  generatedFromRequirementRefs?: string[]
  specApproved?: boolean
  specCycles?: number
  planStale?: boolean
  codeStale?: boolean
  completedAt?: number
  policyOverride?: string
  userStory?: string
  acceptanceCriteria?: string[]
  designRefs?: string[]
  subtasks?: string[]
  createdAt?: number
  updatedAt?: number
}

/** One epic row, mirroring `MongoEpic`'s serialized JSON (a PX module). */
export interface BoardEpicRow {
  id: string
  legacyId: string
  projectId?: string
  title?: string
  description?: string
  color?: string
  status?: string
  priority?: string
  versionId?: string
  dependencies?: string[]
  modules?: string[]
  acceptanceCriteria?: string[]
  ticketIds?: string[]
  createdAt?: number
  /** Computed at read time by `EpicEnrichmentService` — never persisted. */
  progress: number
  completedTickets: number
  totalTickets: number
}

/**
 * One story row, mirroring `MongoStory`. Declared for the feature's type
 * surface (Step 2 of the plan); `GET stories` is explicitly NOT wired in
 * this ticket (no AC requires it — a wired call with no caller is dead
 * code), so no thunk in this feature ever produces one.
 */
export interface BoardStoryRow {
  id: string
  legacyId?: string
  title?: string
  epicId?: string
  status?: string
}

/** One activity row from the project-scoped feed (`MongoActivity`, trimmed to the run log's needs). */
export interface ActivityContextRef {
  type: string
  id: string
}

export interface ActivityAuthor {
  type: string
  id: string
  name: string
}

export interface ActivityRow {
  id: string
  type: string
  content?: string
  author?: ActivityAuthor
  contextIds?: ActivityContextRef[]
  createdAt?: number
  messageType?: string
}

/** The envelope `GET …/projects/:projectId/activity` returns (never a bare array). */
export interface ActivityListResponse {
  items: ActivityRow[]
  nextCursor: string | null
}

/**
 * A keyed refusal — `scope` names the row the refusal belongs to (a
 * `legacyId`). `status` is carried alongside `code`/`message` — a
 * deliberate addition over the shared interface contract's bare
 * `{scope?, code?, message}` — because the page's `<ErrorPanel>` needs a
 * real `status` for `asApiError`; the contract's own pseudo-code names
 * `status: a.status` in the same breath as the narrower type. Reported as
 * an execute-time deviation (also made independently by TASK-60028F48's
 * `features/requirements` on the same shared tree).
 */
export interface KeyedFeatureError {
  scope?: string
  code?: string
  message: string
  status: number
}

/** The typed 403/404 on a feature's primary read (INV-4.5). */
export interface FeatureBlocked {
  status: 403 | 404
  code?: string
  message: string
}

/** Aggregate progress over the loaded tasks — derived, never stored. */
export interface BoardProgress {
  total: number
  done: number
  /** Sum of TaskStatus progress weights ÷ total, 0–1; CANCELLED excluded from both. */
  ratio: number
  byStatus: Record<TaskStatusValue, number>
}

/** Statuses whose next move is a named human's, per the server's own gate verbs. */
export const HUMAN_GATED_STATUSES: readonly TaskStatusValue[] = ['spec', 'review', 'test', 'uat']
